import React, { useState } from 'react';
import config from '../config';

// SVG-Icon für PDF-Export-Modus
const ExportIcon = ({ mode, selected }) => {
  const isSelected = selected === mode;
  const outerFill = '#e0e0e0';
  const innerFill = isSelected ? '#4caf50' : '#bbb';
  const strokeColor = isSelected ? '#4caf50' : '#ccc';
  const strokeWidth = isSelected ? 2.5 : 1.5;

  const getInnerRect = () => {
    switch (mode) {
      case 'viewport':
        return { x: 6, y: 6, w: 40, h: 28 };
      case 'allWeeks':
        return { x: 6, y: 6, w: 88, h: 28 };
      case 'allPanels':
        return { x: 6, y: 6, w: 40, h: 60 };
      case 'full':
        return { x: 6, y: 6, w: 88, h: 60 };
      default:
        return { x: 6, y: 6, w: 40, h: 28 };
    }
  };

  const inner = getInnerRect();

  return (
    <svg width="100" height="72" viewBox="0 0 100 72" style={{ display: 'block' }}>
      <rect
        x="2" y="2" width="96" height="68" rx="4"
        fill={outerFill}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />
      <rect
        x={inner.x} y={inner.y} width={inner.w} height={inner.h} rx="2"
        fill={innerFill}
        stroke={isSelected ? '#2e7d32' : '#999'}
        strokeWidth={isSelected ? 1.5 : 1}
        opacity="0.9"
      />
      {mode === 'viewport' && (
        <>
          <line x1="20" y1="2" x2="20" y2="68" stroke="#ccc" strokeWidth="0.5" strokeDasharray="2,2" />
          <line x1="34" y1="2" x2="34" y2="68" stroke="#ccc" strokeWidth="0.5" strokeDasharray="2,2" />
        </>
      )}
      {mode === 'allPanels' && (
        <>
          <line x1="20" y1="2" x2="20" y2="68" stroke="#ccc" strokeWidth="0.5" strokeDasharray="2,2" />
          <line x1="34" y1="2" x2="34" y2="68" stroke="#ccc" strokeWidth="0.5" strokeDasharray="2,2" />
        </>
      )}
    </svg>
  );
};

// Hilfsfunktion: Serialisiert ein Element mit allen computed styles als Inline-Styles
const serializeElementWithStyles = (element) => {
  const clone = element.cloneNode(true);

  const processNode = (origNode, cloneNode) => {
    if (origNode.nodeType !== 1) return;

    const computed = window.getComputedStyle(origNode);
    const inlineStyles = {};

    // Wichtige Styles kopieren (nur die, die nicht default sind)
    const importantProps = [
      'display', 'position', 'top', 'left', 'right', 'bottom',
      'width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight',
      'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
      'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
      'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
      'borderRadius', 'borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomLeftRadius', 'borderBottomRightRadius',
      'background', 'backgroundColor', 'backgroundImage', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat',
      'color', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textAlign', 'lineHeight',
      'overflow', 'overflowX', 'overflowY',
      'flex', 'flexDirection', 'flexWrap', 'justifyContent', 'alignItems', 'alignContent', 'gap',
      'transform', 'opacity', 'visibility', 'zIndex',
      'boxShadow', 'textShadow',
      'cursor', 'pointerEvents',
      'whiteSpace', 'wordWrap', 'wordBreak',
      'listStyle', 'listStyleType',
    ];

    importantProps.forEach(prop => {
      const value = computed.getPropertyValue(prop);
      if (value && value !== 'initial' && value !== 'auto' && value !== 'normal' && value !== 'none' && value !== '0px') {
        inlineStyles[prop] = value;
      }
    });

    // Bestehende Inline-Styles beibehalten (überschreiben computed)
    if (origNode.style && origNode.style.cssText) {
      const existingStyles = origNode.style.cssText.split(';').filter(s => s.trim());
      existingStyles.forEach(styleDecl => {
        const [prop, val] = styleDecl.split(':').map(s => s.trim());
        if (prop && val) {
          inlineStyles[prop] = val;
        }
      });
    }

    // Styles auf den Klon anwenden
    Object.entries(inlineStyles).forEach(([prop, val]) => {
      cloneNode.style[prop] = val;
    });

    // Rekursiv für Kinder
    for (let i = 0; i < origNode.children.length; i++) {
      processNode(origNode.children[i], cloneNode.children[i]);
    }
  };

  processNode(element, clone);

  // Container mit passenden Styles wrappen
  const wrapper = document.createElement('div');
  wrapper.style.width = element.scrollWidth + 'px';
  wrapper.style.height = element.scrollHeight + 'px';
  wrapper.style.overflow = 'visible';
  wrapper.appendChild(clone);

  return wrapper.outerHTML;
};

const PdfExportDialog = ({ isOpen, onClose, planName = 'Plan', onOpenAllPanels, onRestorePanels }) => {
  const [exportMode, setExportMode] = useState('viewport');
  const [selectedOrientation, setSelectedOrientation] = useState('landscape');
  const [selectedFormat, setSelectedFormat] = useState('a4');
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const modes = [
    { id: 'viewport', label: 'Sichtbar' },
    { id: 'allWeeks', label: 'Alle Wochen' },
    { id: 'allPanels', label: 'Alle Panels' },
    { id: 'full', label: 'Ganzer Plan' },
  ];

  const waitForStableHeight = async (element, timeoutMs = 5000) => {
    let stableCount = 0;
    let lastHeight = element.scrollHeight;
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, 200));
      const currentHeight = element.scrollHeight;
      if (currentHeight === lastHeight) {
        stableCount++;
        if (stableCount >= 3) {
          await new Promise(resolve => setTimeout(resolve, 300));
          return currentHeight;
        }
      } else {
        stableCount = 0;
        lastHeight = currentHeight;
      }
    }
    return lastHeight;
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setProgress(10);

    const targetElement = document.getElementById('plan-export-root');
    if (!targetElement) {
      alert('Plan-Bereich nicht gefunden.');
      setIsExporting(false);
      return;
    }

    const allWeeks = exportMode === 'allWeeks' || exportMode === 'full';
    const allPanels = exportMode === 'allPanels' || exportMode === 'full';

    try {
      // Edit-Mode ausschalten
      window.dispatchEvent(new CustomEvent('pdfExportDisableEditMode'));
      await new Promise(resolve => setTimeout(resolve, 300));

      setProgress(20);

      // Panels öffnen (nur für "Alle Panels" oder "Ganzer Plan")
      if (allPanels && onOpenAllPanels) {
        onOpenAllPanels();
        await waitForStableHeight(targetElement);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setProgress(40);

      // Temporär: Styles setzen basierend auf Export-Modus
      const savedOverflow = targetElement.style.overflow;
      const savedWidth = targetElement.style.width;
      const savedHeight = targetElement.style.height;

      if (allWeeks && allPanels) {
        // Ganzer Plan: alles sichtbar machen
        targetElement.style.overflow = 'visible';
        targetElement.style.width = targetElement.scrollWidth + 'px';
        targetElement.style.height = targetElement.scrollHeight + 'px';
      } else if (allWeeks && !allPanels) {
        // Alle Wochen, nur sichtbare Panels (vertikaler Viewport)
        targetElement.style.overflow = 'hidden';
        targetElement.style.width = targetElement.scrollWidth + 'px';
        targetElement.style.height = targetElement.clientHeight + 'px';
      } else if (!allWeeks && allPanels) {
        // Sichtbare Wochen, alle Panels (horizontaler Viewport)
        targetElement.style.overflow = 'hidden';
        targetElement.style.width = targetElement.clientWidth + 'px';
        targetElement.style.height = targetElement.scrollHeight + 'px';
      } else {
        // Nur aktueller Viewport
        targetElement.style.overflow = 'hidden';
        targetElement.style.width = targetElement.clientWidth + 'px';
        targetElement.style.height = targetElement.clientHeight + 'px';
      }

      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 200));

      setProgress(60);

      // HTML serialisieren mit Inline-Styles
      let htmlContent = serializeElementWithStyles(targetElement);

      // Export-Dimensionen ermitteln (was gerade sichtbar ist)
      const exportWidth = targetElement.clientWidth;
      const exportHeight = targetElement.clientHeight;

      // Styles wiederherstellen
      targetElement.style.overflow = savedOverflow;
      targetElement.style.width = savedWidth;
      targetElement.style.height = savedHeight;

      // Panels wiederherstellen
      if (allPanels && onRestorePanels) {
        onRestorePanels();
      }

      setProgress(75);

      setProgress(80);

      // An Backend senden
      const response = await fetch(`${config.API_HOST}:${config.API_PORT}/api/export/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.API_KEY,
        },
        body: JSON.stringify({
          html: htmlContent,
          width: exportWidth,
          height: exportHeight,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      setProgress(90);

      // PDF downloaden
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safePlanName = (planName || 'Plan').replace(/[^a-z0-9\u00E4\u00F6\u00FC\u00DF\s-]/gi, '_');
      link.download = `${safePlanName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setProgress(100);

      setTimeout(() => {
        setIsExporting(false);
        setProgress(0);
        onClose();
      }, 300);
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      alert('Fehler beim Erstellen des PDFs: ' + error.message);

      // Im Fehlerfall Panels wiederherstellen
      if (allPanels && onRestorePanels) {
        onRestorePanels();
      }
      setIsExporting(false);
      setProgress(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '24px',
        width: '520px',
        maxWidth: '90vw',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#333' }}>
          Plan als PDF exportieren
        </h3>

        {/* 4 Icons nebeneinander */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', justifyContent: 'center' }}>
          {modes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setExportMode(mode.id)}
              disabled={isExporting}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                borderRadius: '8px',
                border: exportMode === mode.id ? '2px solid #4caf50' : '2px solid #ddd',
                backgroundColor: exportMode === mode.id ? '#e8f5e9' : '#f9f9f9',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isExporting ? 0.6 : 1,
                minWidth: '100px'
              }}
            >
              <ExportIcon mode={mode.id} selected={exportMode} />
              <span style={{
                fontSize: '12px',
                color: exportMode === mode.id ? '#2e7d32' : '#666',
                fontWeight: exportMode === mode.id ? 'bold' : 'normal',
                textAlign: 'center',
                lineHeight: 1.3
              }}>
                {mode.label}
              </span>
            </button>
          ))}
        </div>

        {/* Orientierung */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555', fontSize: '14px' }}>
            Orientierung
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setSelectedOrientation('landscape')}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: '6px',
                border: selectedOrientation === 'landscape' ? '2px solid #2196f3' : '2px solid #ddd',
                backgroundColor: selectedOrientation === 'landscape' ? '#e3f2fd' : '#f9f9f9',
                color: selectedOrientation === 'landscape' ? '#1565c0' : '#666',
                fontWeight: selectedOrientation === 'landscape' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              Querformat
            </button>
            <button
              onClick={() => setSelectedOrientation('portrait')}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: '6px',
                border: selectedOrientation === 'portrait' ? '2px solid #2196f3' : '2px solid #ddd',
                backgroundColor: selectedOrientation === 'portrait' ? '#e3f2fd' : '#f9f9f9',
                color: selectedOrientation === 'portrait' ? '#1565c0' : '#666',
                fontWeight: selectedOrientation === 'portrait' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              Hochformat
            </button>
          </div>
        </div>

        {/* Papierformat */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555', fontSize: '14px' }}>
            Papierformat
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setSelectedFormat('a4')}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: '6px',
                border: selectedFormat === 'a4' ? '2px solid #2196f3' : '2px solid #ddd',
                backgroundColor: selectedFormat === 'a4' ? '#e3f2fd' : '#f9f9f9',
                color: selectedFormat === 'a4' ? '#1565c0' : '#666',
                fontWeight: selectedFormat === 'a4' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              A4
            </button>
            <button
              onClick={() => setSelectedFormat('a3')}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: '6px',
                border: selectedFormat === 'a3' ? '2px solid #2196f3' : '2px solid #ddd',
                backgroundColor: selectedFormat === 'a3' ? '#e3f2fd' : '#f9f9f9',
                color: selectedFormat === 'a3' ? '#1565c0' : '#666',
                fontWeight: selectedFormat === 'a3' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              A3
            </button>
          </div>
        </div>

        {/* Fortschrittsbalken */}
        {isExporting && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, backgroundColor: '#4caf50', transition: 'width 0.3s ease' }} />
            </div>
            <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '12px', color: '#888' }}>
              PDF wird erstellt… {progress}%
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={isExporting} style={{
            padding: '10px 20px', borderRadius: '6px', border: '1px solid #ddd',
            backgroundColor: '#f5f5f5', color: '#555',
            cursor: isExporting ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: isExporting ? 0.6 : 1
          }}>
            Abbrechen
          </button>
          <button onClick={handleExport} disabled={isExporting} style={{
            padding: '10px 24px', borderRadius: '6px', border: 'none',
            backgroundColor: isExporting ? '#aaa' : '#4caf50', color: '#fff',
            cursor: isExporting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold'
          }}>
            {isExporting ? 'Erstelle PDF…' : 'PDF erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PdfExportDialog;
